import { z } from 'zod';
import type { RetiroDTO } from './caja.ts';
import { paginacion } from './comun.ts';

// El cierre es en dos pasos (bloque 8, 2026-10-09): `declararConteoInput` y `cerrarConDiferenciaInput` en ./caja.ts

export const reabrirSesionInput = z.object({ motivo: z.string().trim().min(5, 'Indica el motivo').max(300) });
export const cierreSucursalInput = z.object({ fecha: z.iso.date() });
/** Lista de cierres de sucursal: «Desde / Hasta» (fecha del cierre) y páginas de 25 (hallazgo T19). */
export const cierresSucursalFiltros = paginacion.extend({ desde: z.iso.date().optional(), hasta: z.iso.date().optional() });

export interface FilaMetodoDTO {
  metodo: string;
  moneda: 'USD' | 'BS';
  /** Suma en la moneda del método. */
  monto: string;
  /** Equivalente en $ (a la tasa de cada documento). */
  monto_usd: string;
  operaciones: number;
}

export interface RangoCorrelativoDTO {
  tipo: string;
  desde: string | null;
  hasta: string | null;
  cantidad: number;
  /** Números faltantes dentro del rango (deben ser 0 — Invariante 2). */
  saltos: string[];
  /** Números anulados por hoja dañada dentro del rango (cuentan en el consecutivo, valen cero). Ausente en cierres viejos. */
  anulados?: string[];
  /** Fila CTRL: el paso de un talonario al siguiente dentro del turno («Cambio de talonario: del … al …»; no es salto). */
  notas?: string[];
}

export interface ReporteCierreDTO {
  /** 'fiscal' = solo FAC/NC/ND · 'completo' = FAC + NOTA + abonos + egresos */
  tipo: 'fiscal' | 'completo';
  documentos: number;
  base: string;
  iva: string;
  total: string;
  total_bs: string;
  /** Base, exento e IVA en Bs fiscales (hallazgo B5 del 2026-10-18). Ausentes en cierres anteriores. */
  base_bs?: string;
  exento_bs?: string;
  iva_bs?: string;
  cobrado_usd: string;
  por_metodo: FilaMetodoDTO[];
  correlativos: RangoCorrelativoDTO[];
  abonos_usd: string;
  egresos_usd: string;
  /** Reintegros en efectivo por devoluciones a consumidor final (spec 08). */
  reintegros_usd: string;
  /**
   * IGTF percibido en la sesión: el FISCAL, el de FAC/NC/ND (también el de una factura pagada con saldo a favor, que
   * cuenta como divisas); el COMPLETO, además el de los anticipos anteriores al 2026-10-19 (desde entonces el anticipo no
   * lleva IGTF: pedido de la dueña). Ausente en cierres viejos.
   */
  igtf_usd?: string;
  /** El mismo IGTF en Bs, a la BCV de cada cobro (como se declara). Ausente en cierres anteriores al 2026-10-18. */
  igtf_bs?: string;
  /** Vuelto entregado en $ (el de $ más el de Bs): `cobrado_usd` es lo recibido por método; lo cobrado de verdad es la resta. Ausente en cierres anteriores al 2026-10-18. */
  vuelto_total_usd?: string;
  /**
   * Vueltos de la sesión (solo reporte completo): lo devuelto en $ y en Bs en efectivo sale de la caja; lo devuelto por
   * pago móvil o transferencia sale del banco. Ausente en cierres anteriores al 2026-10-09.
   */
  vueltos?: { usd: string; bs_efectivo: string; bs_pago_movil: string; bs_transferencia: string; cobros_con_sobrante: number };
  /** Sobrante por vuelto: lo que no se devolvió del excedente y quedó en caja, en $. */
  sobrante_usd?: string;
  /** Retiros parciales de efectivo de la sesión, en $ equivalente (solo reporte completo). No son un gasto. */
  retiros_usd?: string;
}

/** Un documento de la sesión con lo que se cobró por cada método (reporte de cierre por documento). */
export interface CierreDocumentoDTO {
  id: string;
  tipo: string;
  numero_formateado: string;
  cliente_razon_social: string;
  estado: 'emitido' | 'anulado';
  total: string;
  /** Lo cobrado en esta sesión por método, en $ equivalente, con las referencias escritas al cobrar. */
  pagos: { metodo: string; monto: string; monto_usd: string; referencias?: string | null }[];
  /** El documento se emitió en otra sesión: aquí solo se le cobró un abono. */
  de_otra_sesion?: boolean;
}

/**
 * Un abono a saldo a favor recibido en la sesión (respuesta de la dueña del 26-10-2026): a quién se acreditó, con qué
 * recibo y con qué se pagó. El reporte COMPLETO solo trae la suma (`abonos_usd`).
 */
export interface CierreAbonoDTO {
  id: string;
  recibo: string;
  recibido_en: string;
  cliente_razon_social: string;
  total_usd: string;
  /** Cada forma de pago del abono, en su moneda, con la referencia escrita al cobrar. */
  pagos: { metodo: string; moneda: 'USD' | 'BS'; monto: string; monto_usd: string; referencia: string | null }[];
}

export interface ArqueoDTO {
  fondo_usd: string;
  fondo_bs: string;
  /** Efectivo que DEBERÍA haber (fondo + cobros − vueltos − egresos), por moneda. */
  sistema_efectivo_usd: string;
  sistema_efectivo_bs: string;
  declarado_efectivo_usd: string | null;
  declarado_efectivo_bs: string | null;
  /** (declarado − sistema) en $ equivalente (Bs a la tasa BCV vigente al cierre). */
  diferencia_usd: string | null;
  /** Σ |diferencia por método| en $: un faltante en efectivo no se compensa con un sobrante en Zelle (umbral). */
  diferencia_abs_usd?: string | null;
  tasa_bcv_cierre: string;
  /** Tasa 2 vigente al cerrar: convierte los Bs del arqueo a $ reales (regla de precios 2026-10-09). */
  tasa_paralela_cierre: string;
  umbral_usd: string;
  requiere_aprobacion: boolean;
  /** Otros métodos (verificables por banco): sistema vs declarado (informativo). */
  otros: { metodo: string; moneda: 'USD' | 'BS'; sistema: string; declarado: string | null }[];
  /**
   * Arqueo por método declarado por el cajero (efectivo $, efectivo Bs, Zelle, USDT, punto y, mientras no haya
   * verificación bancaria, transferencia y pago móvil). Ausente en cierres guardados antes del 24-09-2026.
   */
  por_metodo?: ArqueoMetodoDTO[];
  /** Retiros parciales de la sesión por moneda: ya están descontados del efectivo que el sistema espera. */
  retiros_usd?: string;
  retiros_bs?: string;
  /** Los pagos bancarios se declararon en este cierre (config `cierre_declara_bancarios`). */
  declara_bancarios?: boolean;
  /** Lotes del punto de venta declarados (número y monto); su suma es lo declarado en punto. */
  lotes_punto?: { numero: string; monto: string }[];
  /** Banco del punto de venta de la caja. */
  punto_banco?: string | null;
}

export interface ArqueoMetodoDTO {
  metodo: string;
  moneda: 'USD' | 'BS';
  fondo: string;
  sistema: string;
  sistema_sin_fondo: string;
  declarado: string | null;
  declarado_sin_fondo: string | null;
  /** declarado − sistema en la moneda del método. */
  diferencia: string | null;
  /** La misma diferencia en $ (Bs a la tasa BCV del cierre). */
  diferencia_usd: string | null;
}

export interface CierreSesionDTO {
  sesion_id: string;
  caja_nombre: string;
  usuario_nombre: string;
  sucursal_nombre: string;
  abierta_en: string;
  cerrada_en: string | null;
  estado: 'abierta' | 'cerrada';
  cerrada_por_nombre: string | null;
  aprobado_por_nombre: string | null;
  justificacion_diferencia: string | null;
  reaperturas: number;
  reabierta_por_nombre: string | null;
  motivo_reapertura: string | null;
  esperas_pendientes: number;
  esperas_purgadas: number;
  fiscal: ReporteCierreDTO;
  completo: ReporteCierreDTO;
  arqueo: ArqueoDTO;
  /** true cuando se lee del snapshot (cerrada); false cuando es cálculo en vivo (abierta). */
  desde_snapshot: boolean;
  impresiones: number;
  /** Turno del día en esa caja (1 = primera sesión del día). */
  turno?: number;
  /** El cajero ya declaró su conteo a ciegas (la caja no vende más; falta justificar o autorizar si hay diferencia). */
  declarado?: boolean;
  declarado_en?: string | null;
  /** Veces que se permitió volver a contar. */
  reconteos?: number;
  /** Retiros parciales de efectivo de la sesión. */
  retiros?: RetiroDTO[];
  /** Documentos de la sesión con lo cobrado por método. */
  documentos?: CierreDocumentoDTO[];
  /** Abonos a saldo a favor de la sesión, uno por uno (el recuadro «Abonos del turno»). */
  abonos?: CierreAbonoDTO[];
  /** Observación escrita al declarar el conteo (o al cerrar); se borra al reabrir o recontar y queda en la auditoría. */
  observacion?: string | null;
}

export interface CierreSucursalDTO {
  id: string;
  fecha: string;
  cerrado_por_nombre: string;
  cerrado_en: string;
  /** Sucursal del cierre (sin sucursal elegida, la lista trae las de todas). */
  sucursal_nombre?: string;
  sesiones: { sesion_id: string; caja_nombre: string; usuario_nombre: string; estado: string; cobrado_usd: string; diferencia_usd: string | null; turno?: number; retiros_usd?: string; retiros_sin_confirmar?: number }[];
  cajas_sin_cerrar: number;
  total_cobrado_usd: string;
  total_fiscal_usd: string;
  total_no_fiscal_usd: string;
  /** Retiros parciales de efectivo del día, en $ equivalente. */
  total_retiros_usd?: string;
  /** Retiros del día que la dueña aún no confirmó (o dejó observados). */
  retiros_sin_confirmar?: number;
  /**
   * Documentos del día emitidos sin caja (los hace el administrador sin abrir caja): no están en ningún cierre de
   * caja. `documentos` y `fiscal_usd` son lo fiscal (factura + nota de débito − nota de crédito); lo no fiscal
   * (nota − cambio) va aparte. Los cierres anteriores al 2026-10-10 no traen la parte no fiscal.
   */
  fuera_de_caja?: {
    documentos: number;
    fiscal_usd: string;
    documentos_no_fiscales?: number;
    no_fiscal_usd?: string;
    /** Hojas en blanco anuladas ese día sin caja abierta (desde la migración 064; hallazgo T11). */
    hojas_anuladas?: string[];
  };
  /**
   * Turnos de ese día que se reabrieron después del cierre de sucursal (decisión del 2026-10-27): el cierre no se rehace;
   * al pie dice lo cobrado al cerrar la sucursal y lo de ahora (null si el turno sigue abierto). Se calcula al leer.
   */
  turnos_reabiertos?: { sesion_id: string; caja_nombre: string; usuario_nombre: string; antes_usd: string; ahora_usd: string | null; reabierta_en: string }[];
}
